import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AlertModalConfig {
  type: 'success' | 'error' | 'confirm';
  message: string;
  title?: string;
  navigateTo?: string;
  onConfirm?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class AlertModalService {
  private alertSubject = new BehaviorSubject<AlertModalConfig | null>(null);
  public alert$ = this.alertSubject.asObservable();

  constructor(private router: Router) {}

  /**
   * Show success alert modal
   * @param message - Message to display
   * @param navigateTo - Optional route to navigate after OK
   */
  showSuccess(message: string, navigateTo?: string): void {
    this.alertSubject.next({
      type: 'success',
      title: 'Success',
      message,
      navigateTo
    });
  }

  /**
   * Show error alert modal
   * @param message - Error message to display
   */
  showError(message: string): void {
    this.alertSubject.next({
      type: 'error',
      title: 'Error',
      message
    });
  }

  /**
   * Show confirmation dialog
   * @param message - Confirmation message
   * @param onConfirm - Callback when user confirms
   */
  showConfirm(message: string, onConfirm: () => void): void {
    this.alertSubject.next({
      type: 'confirm',
      title: 'Confirm',
      message,
      onConfirm
    });
  }

  /**
   * Close the current alert modal
   */
  closeAlert(): void {
    this.alertSubject.next(null);
  }

  /**
   * Handle OK button click with optional navigation
   */
  handleOk(navigateTo?: string): void {
    if (navigateTo) {
      this.router.navigate([navigateTo]);
    }
    this.closeAlert();
  }
}
